import { ChevronRight, ExternalLink } from 'lucide-react';
import { kindInfo, linkLabel } from '../../lib/bookshelf';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};

// Generated cover: kind colour, a darker spine, the publisher's short name.
// No images, so nothing to upload or crop (decided on the 2026-10-27 mockup).
// The kind is NOT printed on the cover: it already sits beside it in the meta
// row, and "PROGRAM" clipped at 58px on a phone.
const Cover = ({ item }) => (
  <div
    aria-hidden="true"
    className="relative w-[58px] h-[78px] text-white flex-shrink-0"
    style={{
      backgroundColor: kindInfo(item.kind).color,
      borderRadius: '4px 8px 8px 4px',
      padding: '8px 6px 7px 12px',
      boxShadow: '0 2px 6px rgba(26,46,34,0.18)',
    }}
  >
    <span className="absolute left-0 top-0 bottom-0 w-[6px]" style={{ backgroundColor: 'rgba(0,0,0,0.22)', borderRadius: '4px 0 0 4px' }} />
    <span className="block text-[10.5px] font-extrabold leading-tight tracking-wide break-words">{item.publisherShort}</span>
  </div>
);

const BookshelfCard = ({ item }) => {
  const kind = kindInfo(item.kind);
  // Short, link-less lists (the nine tenets) sit in two columns on desktop;
  // article lists with links stay one per line.
  const twoCol = item.highlights.length > 4
    && item.highlights.every((h) => !h.url && h.title.length <= 45);

  return (
    // Phone: the cover sits beside the header only (type, publisher, title) and
    // the summary runs the card's full width below, because beside an 80px
    // cover it wrapped every few words. From md the cover spans both rows.
    <article className="rounded-2xl p-4 grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-2.5" style={cardStyle}>
      <div className="md:row-span-2"><Cover item={item} /></div>

      <div className="min-w-0 flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]" style={{ color: 'var(--text-tertiary)' }}>
          <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: kind.color }}>{kind.label}</span>
          <span>{item.publisher}</span>
          {item.sponsor && (
            <span
              className="text-[11px] font-semibold px-1.5 py-px rounded-full"
              style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: 'var(--text-secondary)' }}
            >
              Founding Sponsor
            </span>
          )}
        </div>
        <h3 className="text-base font-bold leading-snug" style={{ color: 'var(--text-primary)', textWrap: 'balance' }}>
          {item.title}
        </h3>
        {item.edition && (
          <p className="text-[12.5px]" style={{ color: 'var(--text-tertiary)' }}>{item.edition}</p>
        )}
      </div>

      <div className="col-span-2 md:col-span-1 md:col-start-2 min-w-0 flex flex-col gap-1.5">
        <p className="text-[15px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{item.summary}</p>
        {item.note && (
          <p className="text-[13px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>{item.note}</p>
        )}
      </div>

      {item.highlights.length > 0 && (
        <details className="group col-span-2 border-t pt-2" style={{ borderColor: 'var(--card-border)' }}>
          <summary
            className="flex items-center gap-1.5 text-[13.5px] font-semibold cursor-pointer list-none [&::-webkit-details-marker]:hidden"
            style={{ color: 'var(--brand-primary)' }}
          >
            <ChevronRight size={16} className="transition-transform group-open:rotate-90 motion-reduce:transition-none" />
            {item.highlightsLabel || "What's inside"}
          </summary>
          <ul className={`mt-2 grid gap-1.5 ${twoCol ? 'md:grid-cols-2 md:gap-x-5' : ''}`}>
            {item.highlights.map((h, i) => (
              <li key={i} className="relative pl-3.5 text-sm leading-snug" style={{ color: 'var(--text-secondary)' }}>
                <span
                  className="absolute left-0.5 top-[0.55em] w-[5px] h-[5px] rounded-full opacity-60"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                />
                {h.url ? (
                  <a
                    href={h.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2"
                    style={{ color: 'var(--text-primary)', textDecorationColor: 'rgba(77,119,94,0.45)' }}
                  >
                    {h.title}
                  </a>
                ) : h.title}
              </li>
            ))}
          </ul>
        </details>
      )}

      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="col-span-2 justify-self-start inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98]"
        style={{ backgroundColor: 'var(--brand-primary)' }}
      >
        {linkLabel(item)}
        <ExternalLink size={14} />
      </a>
    </article>
  );
};

export default BookshelfCard;
